/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import React, { ReactElement } from 'react';

import classnames from 'classnames';

import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Class names used in component.
 */
const componentClassName = 'msc-table';
const headerClassName = `${componentClassName}__header`;
const headerRowClassName = `${componentClassName}__header-row`;
const headerCellClassName = `${componentClassName}__header-cell`;
const rowClassName = `${componentClassName}__row`;
const rowCellClassName = `${componentClassName}__row-cell`;

/**
 * Table cell content type.
 */
export enum TableCellContentType {
    Text = 'text',
    Number = 'number',
    Amount = 'amount',
    Date = 'date'
}

/**
 * ITableComponent interface.
 */
interface ITableComponent extends IComponent<ITableProps> {}

/**
 * ITableHeaderComponent interface.
 */
interface ITableHeaderComponent extends IComponent<ITableHeaderProps> {}

/**
 * ITableRowComponent interface.
 */
interface ITableRowComponent extends IComponent<ITableRowProps> {}

/**
 * ITableProps interface.
 */
export interface ITableProps extends IComponentProps<{}> {
    className?: string;
    children?: React.ReactNode | React.ReactNode[];
}

/**
 * ITableHeaderProps interface.
 */
export interface ITableHeaderProps extends IComponentProps<{}> {
    className?: string;
    isVisible?: boolean;
    children?: ReactElement<ITableCellProps> | ReactElement<ITableCellProps>[];
}

/**
 * ITableRowProps interface.
 */
export interface ITableRowProps extends IComponentProps<{}> {
    className?: string;
    isVisible?: boolean;
    children?: ReactElement<ITableCellProps> | ReactElement<ITableCellProps>[];
}

/**
 * ITableCellProps interface.
 */
export interface ITableCellProps extends IComponentProps<{}> {
    className?: string;
    isVisible?: boolean;
    children?: React.ReactNode;
    contentType: TableCellContentType;
}

/**
 * TableHeader component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const TableHeaderFunctionalComponent: React.FC<ITableHeaderProps> = (props: ITableHeaderProps) => {
    const isVisible = props.isVisible ?? true;
    if (!isVisible) {
        return <></>;
    }

    let children = props.children ?? [];
    children = Array.isArray(children) ? children : [children];

    return (
        <thead className={classnames(props.className, headerClassName)}>
            <tr className={headerRowClassName}>
                {ArrayExtensions.hasElements(children) &&
                    children.map((child, index) => {
                        const childIsVisible = child.props.isVisible ?? true;
                        return (
                            childIsVisible && (
                                <th key={index} className={classnames(child.props.className, headerCellClassName, child.props.contentType)}>
                                    {child.props.children}
                                </th>
                            )
                        );
                    })}
            </tr>
        </thead>
    );
};

/**
 * TableHeader component.
 */
export const TableHeaderComponent: React.FunctionComponent<ITableHeaderProps> = msdyn365Commerce.createComponent<ITableHeaderComponent>(
    'TableHeaderComponent',
    { component: TableHeaderFunctionalComponent }
);

/**
 * TableRow component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const TableRowFunctionalComponent: React.FC<ITableRowProps> = (props: ITableRowProps) => {
    const isVisible = props.isVisible ?? true;
    if (!isVisible) {
        return <></>;
    }

    let children = props.children ?? [];
    children = Array.isArray(children) ? children : [children];

    return (
        <tr className={classnames(props.className, rowClassName)}>
            {ArrayExtensions.hasElements(children) &&
                children.map((child, index) => {
                    const childIsVisible = child.props.isVisible ?? true;
                    return (
                        childIsVisible && (
                            <td key={index} className={classnames(child.props.className, rowCellClassName, child.props.contentType)}>
                                {child.props.children}
                            </td>
                        )
                    );
                })}
        </tr>
    );
};

/**
 * TableRow component.
 */
export const TableRowComponent: React.FunctionComponent<ITableRowProps> = msdyn365Commerce.createComponent<ITableRowComponent>(
    'TableRowComponent',
    { component: TableRowFunctionalComponent }
);

/**
 * TableCell component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const TableCellFunctionalComponent: React.FC<ITableCellProps> = (props: ITableCellProps) => {
    return <>{props.children}</>;
};

/**
 * TableCell component.
 */
export const TableCellComponent: React.FunctionComponent<ITableCellProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    ITableCellComponent
>('TableCellComponent', { component: TableCellFunctionalComponent });

/**
 * Table component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const TableFunctionalComponent: React.FC<ITableProps> = (props: ITableProps) => {
    return <table className={classnames(props.className, componentClassName)}>{props.children}</table>;
};

/**
 * Table component.
 */
export const TableComponent: React.FunctionComponent<ITableProps> = msdyn365Commerce.createComponent<ITableComponent>('TableComponent', {
    component: TableFunctionalComponent
});
